import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import apiClient from "../utils/apiClient";
import styled from "@emotion/styled";
import { client, urlFor } from "../sanity/sanityClient";
import { host } from "../utils/APIRoutes";
import { toggleFollow, checkIsFollowing } from "../services/feed";
import { FiMenu, FiUserPlus, FiUserCheck } from "react-icons/fi";
import { AiOutlineEye } from "react-icons/ai";

/**
 * Live Discovery — the screen shoppers land on when multiple
 * merchants may be live simultaneously. Lists every active
 * liveSession, with streams from hosts the viewer already follows
 * sorted above everyone else's, and a Follow button on the rest so a
 * shopper can follow directly from here without leaving the list.
 *
 * This is the correct entry point for the "Live" bottom-nav tab and
 * the UploadForm "LIVE" badge now — Live.js itself no longer guesses
 * "most recent session," it takes an explicit sessionId from the
 * route (see Live.js's useParams change).
 */
export default function LiveDiscovery() {
  const navigate = useNavigate();

  const [currentUser, setCurrentUser] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [followingMap, setFollowingMap] = useState({}); // hostUserId -> boolean
  const [followBusyId, setFollowBusyId] = useState(null);
  const [viewerCounts, setViewerCounts] = useState({}); // sessionId -> count
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const userData = localStorage.getItem(process.env.REACT_APP_LOCALHOST_KEY);
    if (userData) setCurrentUser(JSON.parse(userData));
  }, []);

  // ---------------------------------------------------------------
  // Load all active sessions + this viewer's follow status for each
  // distinct host, then sort followed-hosts-first.
  // ---------------------------------------------------------------
  const loadSessions = useCallback(async () => {
    try {
      const docs = await client.fetch(
        `*[_type == "liveSession" && isLive == true] | order(startTime desc){
          _id, title, hostUserId, hostName, hostAvatarUrl, coverImage, currency, startTime, seedViewerCount
        }`
      );

      if (currentUser?._id) {
        const uniqueHostIds = [...new Set(docs.map((d) => d.hostUserId))];
        const pairs = await Promise.all(
          uniqueHostIds.map((hostId) =>
            checkIsFollowing(currentUser._id, hostId)
              .then((isFollowing) => [hostId, isFollowing])
              .catch(() => [hostId, false])
          )
        );
        const map = Object.fromEntries(pairs);
        setFollowingMap(map);

        // Followed hosts first, most-recently-started within each group.
        const sorted = [...docs].sort((a, b) => {
          const aFollowed = map[a.hostUserId] ? 1 : 0;
          const bFollowed = map[b.hostUserId] ? 1 : 0;
          if (aFollowed !== bFollowed) return bFollowed - aFollowed;
          return new Date(b.startTime) - new Date(a.startTime);
        });
        setSessions(sorted);
      } else {
        setSessions(docs);
      }
    } catch (err) {
      console.error("Failed to load live sessions:", err);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  // ---------------------------------------------------------------
  // Viewer counts — a lightweight poll against a dedicated endpoint
  // rather than joining every session's socket room just to display a
  // number (see live_discovery_server.js for the endpoint).
  // ---------------------------------------------------------------
  useEffect(() => {
    if (sessions.length === 0) return;

    const fetchCounts = async () => {
      try {
        const ids = sessions.map((s) => s._id).join(",");
        const { data } = await apiClient.get(`${host}/api/live/viewer-counts`, {
          params: { sessionIds: ids },
        });
        setViewerCounts(data || {});
      } catch (err) {
        console.error("Failed to fetch viewer counts:", err);
      }
    };

    fetchCounts();
    const interval = setInterval(fetchCounts, 15000);
    return () => clearInterval(interval);
  }, [sessions]);

  // Re-poll the session list itself periodically too, so a stream
  // that just went live (or just ended) appears/disappears without a
  // manual refresh.
  useEffect(() => {
    const interval = setInterval(loadSessions, 30000);
    return () => clearInterval(interval);
  }, [loadSessions]);

  const handleFollowClick = async (e, hostUserId) => {
    e.stopPropagation();
    if (!currentUser || followBusyId) return;
    if (!currentUser._id || currentUser._id === hostUserId) return;

    setFollowBusyId(hostUserId);
    const optimistic = !followingMap[hostUserId];
    setFollowingMap((prev) => ({ ...prev, [hostUserId]: optimistic }));

    try {
      const result = await toggleFollow(currentUser._id, hostUserId);
      setFollowingMap((prev) => ({ ...prev, [hostUserId]: result.following }));
    } catch (err) {
      console.error("Follow toggle failed:", err);
      setFollowingMap((prev) => ({ ...prev, [hostUserId]: !optimistic }));
    } finally {
      setFollowBusyId(null);
    }
  };

  return (
    <Container>
      <TopBar>
        <FiMenu size={22} onClick={() => navigate(-1)} style={{ cursor: "pointer" }} />
        <Logo>
          Bridgen <span>Live</span>
        </Logo>
        <div style={{ width: 22 }} />
      </TopBar>

      {loading ? (
        <CenterMessage>Loading live streams…</CenterMessage>
      ) : sessions.length === 0 ? (
        <CenterMessage>
          No one is live right now — check back soon!
          <br />
          <Link to="/" style={{ color: "#f97316", fontWeight: 600 }}>
            Browse products instead
          </Link>
        </CenterMessage>
      ) : (
        <SessionGrid>
          {sessions.map((session) => {
            const isOwnStream = currentUser?._id === session.hostUserId;
            const isFollowed = !!followingMap[session.hostUserId];
            const coverUrl = session.coverImage ? urlFor(session.coverImage) : null;

            return (
              <SessionCard key={session._id} onClick={() => navigate(`/live/${session._id}`)}>
                <ThumbnailWrapper>
                  {coverUrl ? (
                    <Thumbnail src={coverUrl} alt={session.title} />
                  ) : (
                    <ThumbnailFallback />
                  )}
                  <LiveBadge>LIVE</LiveBadge>
                  <ViewerPill>
                    <AiOutlineEye size={12} />
                    {(viewerCounts[session._id] ?? session.seedViewerCount ?? 0) >= 1000
                      ? `${((viewerCounts[session._id] ?? 0) / 1000).toFixed(1)}K`
                      : viewerCounts[session._id] ?? session.seedViewerCount ?? 0}
                  </ViewerPill>
                  {isFollowed && <FollowingBadge>Following</FollowingBadge>}
                </ThumbnailWrapper>

                <CardBody>
                  <HostRow>
                    {session.hostAvatarUrl ? (
                      <HostAvatar
                        src={
                          session.hostAvatarUrl.startsWith("http")
                            ? session.hostAvatarUrl
                            : `data:image/svg+xml;base64,${session.hostAvatarUrl}`
                        }
                        alt={session.hostName}
                      />
                    ) : (
                      <HostAvatarPlaceholder>
                        {session.hostName?.charAt(0)?.toUpperCase()}
                      </HostAvatarPlaceholder>
                    )}
                    <HostName>{session.hostName}</HostName>

                    {!isOwnStream && currentUser && (
                      <FollowButton
                        followed={isFollowed}
                        disabled={followBusyId === session.hostUserId}
                        onClick={(e) => handleFollowClick(e, session.hostUserId)}
                      >
                        {isFollowed ? <FiUserCheck size={12} /> : <FiUserPlus size={12} />}
                        {isFollowed ? "Following" : "Follow"}
                      </FollowButton>
                    )}
                  </HostRow>
                  <StreamTitle>{session.title}</StreamTitle>
                </CardBody>
              </SessionCard>
            );
          })}
        </SessionGrid>
      )}
    </Container>
  );
}

/* ================= STYLES ================= */

const Container = styled.div`
  min-height: 100vh;
  background: #f8f9fb;
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 18px;
  background: white;
  border-bottom: 1px solid #eee;
`;

const Logo = styled.div`
  font-size: 18px;
  font-weight: 800;
  color: #111;
  span {
    color: #f97316;
  }
`;

const CenterMessage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 60vh;
  text-align: center;
  color: #666;
  padding: 20px;
`;

const SessionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 14px;
  padding: 16px;

  @media screen and (min-width: 720px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const SessionCard = styled.div`
  background: white;
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
`;

const ThumbnailWrapper = styled.div`
  position: relative;
  aspect-ratio: 3 / 4;
  background: #ddd;
`;

const Thumbnail = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const ThumbnailFallback = styled.div`
  width: 100%;
  height: 100%;
  background: linear-gradient(160deg, #d9a679, #e8d8d2);
`;

const LiveBadge = styled.div`
  position: absolute;
  top: 8px;
  left: 8px;
  background: #f97316;
  color: white;
  font-size: 10px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 10px;
`;

const ViewerPill = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  display: flex;
  align-items: center;
  gap: 3px;
  background: rgba(0, 0, 0, 0.55);
  color: white;
  font-size: 10px;
  font-weight: 600;
  padding: 2px 7px;
  border-radius: 10px;
`;

const FollowingBadge = styled.div`
  position: absolute;
  bottom: 8px;
  left: 8px;
  background: #2563eb;
  color: white;
  font-size: 9px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 10px;
`;

const CardBody = styled.div`
  padding: 8px;
`;

const HostRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const HostAvatar = styled.img`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
`;

const HostAvatarPlaceholder = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #f97316;
  color: white;
  font-size: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const HostName = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: #333;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
`;

const FollowButton = styled.button`
  display: flex;
  align-items: center;
  gap: 3px;
  background: ${(props) => (props.followed ? "#f1f1f1" : "#f97316")};
  color: ${(props) => (props.followed ? "#333" : "white")};
  border: none;
  border-radius: 10px;
  padding: 3px 7px;
  font-size: 9px;
  font-weight: 700;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  flex-shrink: 0;
`;

const StreamTitle = styled.p`
  font-size: 11px;
  color: #555;
  margin: 6px 0 0;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;
